// components/modals/AppointmentModal.js - Updated with prefilled customer support
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from '../common';
import { useCrewMembersPG } from '../../hooks/useCrewMembersPG';
import { useTranslations } from '../../hooks/useTranslations';
import { useCustomers } from '../../context/CustomerContext';
import { useAutomations } from '../../hooks/useAutomations';
import { usePaymentStatus } from '../../hooks/usePaymentStatus';
import { PaymentAPI } from '../../services/api/paymentAPI';
import { useAuth } from '../../context/AuthContext';
import { useAppointmentsPG } from '../../hooks/useAppointmentsPG';
import { useBusinessConfig } from '../../context/BusinessConfigContext';
import { getErrorMessage } from '../../utils/errorMessages';
import { uploadInvoiceToStorage } from '../../utils/invoiceUpload';
import { AppointmentAPI } from '../../services/api/appointmentAPI';
import { isValidIsraeliPhone, normalizePhone } from '../../utils/validation';
import { Service } from '../../models/Service';
import { CustomerDetailModal } from './CustomerDetailModal';
import { CustomerModal } from './CustomerModal';

import AppointmentModalHeader from '../appointments/AppointmentModalHeader';
import AppointmentFormNotifications from '../appointments/AppointmentFormNotifications';
import CustomerSelector from '../appointments/CustomerSelector';
import ServiceCrewSelector from '../appointments/ServiceCrewSelector';
import AppointmentScheduleFields from '../appointments/AppointmentScheduleFields';
import RecurringAppointmentConfig from '../appointments/RecurringAppointmentConfig';
import AppointmentPaymentSection from '../appointments/AppointmentPaymentSection';
import AutomationExecutions from '../appointments/AutomationExecutions';
import AppointmentDeleteModal from '../appointments/AppointmentDeleteModal';

export function AppointmentModal({
  isOpen,
  onClose,
  onSuccess,
  onCustomerUpdated,
  appointment = null,
  mode = 'add',
  prefilledDate = '',
  prefilledTime = '',
  prefilledCustomerId = '',
  services = []
}) {
  const [formData, setFormData] = useState({
    customer_id: '',
    service_ids: [],  // Changed to array for multi-service
    crew_member_id: '',
    date: '',
    time: '',
    duration: '',
    price: '',
    status: 'scheduled',
    notes: '',
    is_recurring: false,
    recurring_interval_weeks: 1,
    recurring_end_type: 'count', // 'count' or 'date'
    recurring_end_count: 1,
    recurring_end_date: '',
    is_paid: false
  });
  const [selectedServices, setSelectedServices] = useState([]);  // Store full service objects
  const t = useTranslations();
  const { config } = useBusinessConfig()
  const { user } = useAuth();
  const {
    payments,
    paymentStatus,
    loading: paymentLoading,
    error: paymentError
  } = usePaymentStatus(appointment?.id);

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorModalContent, setErrorModalContent] = useState({ title: '', message: '' });
  const [isDeleting, setIsDeleting] = useState(false);
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [selectedCustomerName, setSelectedCustomerName] = useState('');
  const [availabilityConflict, setAvailabilityConflict] = useState(null);
  const [seriesEditMode, setSeriesEditMode] = useState('single'); // 'single', 'future', 'all'
  const [showSeriesOptions, setShowSeriesOptions] = useState(false);
  const [seriesDeleteMode, setSeriesDeleteMode] = useState('single');
  const [skippedPaidInfo, setSkippedPaidInfo] = useState(null);
  const [isCustomerDetailModalOpen, setIsCustomerDetailModalOpen] = useState(false);
  const [isAddCustomerModalOpen, setIsAddCustomerModalOpen] = useState(false);
  const [isEditCustomerModalOpen, setIsEditCustomerModalOpen] = useState(false);
  const customerSearchRef = useRef(null);
  const customerDropdownRef = useRef(null);
  const serviceSelectRef = useRef(null);
  const crewMemberRef = useRef(null);
  const dateRef = useRef(null);
  const timeRef = useRef(null);
  const durationRef = useRef(null);
  const priceRef = useRef(null);

  const [paymentActionLoading, setPaymentActionLoading] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState('');
  const [invoiceItemText, setInvoiceItemText] = useState('');
  const [useAlternatePaymentPhone, setUseAlternatePaymentPhone] = useState(false);
  const [alternatePaymentPhone, setAlternatePaymentPhone] = useState('');
  const [alternatePaymentPhoneError, setAlternatePaymentPhoneError] = useState('');
  const [maxPayments, setMaxPayments] = useState(1);

  // Invoice upload state
  const [invoiceFile, setInvoiceFile] = useState(null);
  const [uploadingInvoice, setUploadingInvoice] = useState(false);
  const [invoiceJustUploaded, setInvoiceJustUploaded] = useState(false);
  const [showInvoiceNumberModal, setShowInvoiceNumberModal] = useState(false);
  const [pendingInvoiceFile, setPendingInvoiceFile] = useState(null);
  const [manualInvoiceNumber, setManualInvoiceNumber] = useState('');

  const { getAppointment, invalidateCacheForDate, forceRefreshCurrentRange } = useAppointmentsPG();
  const [refreshingAppointment, setRefreshingAppointment] = useState(false);

  // Check if the appointment should skip payment display (e.g., breaks, blocked time)
  // Check selected services in add mode, or appointment.service_name in edit mode
  const shouldSkipPayment = React.useMemo(() => {
    const servicesWithoutPayment = Service.getServicesWithoutPayment();
    // In edit mode, check the appointment's service name
    if (mode === 'edit' && appointment?.service_name) {
      return servicesWithoutPayment.includes(appointment.service_name);
    }
    // In add mode or when services are selected, check selected services
    if (selectedServices.length > 0) {
      return selectedServices.some(svc => servicesWithoutPayment.includes(svc.name));
    }
    return false;
  }, [mode, appointment?.service_name, selectedServices]);


  // NEW: Add payment action handlers
  const handleRequestPayment = async (appointmentData) => {
    try {
      // Validate alternate payment phone if enabled
      if (useAlternatePaymentPhone) {
        if (!alternatePaymentPhone.trim()) {
          setAlternatePaymentPhoneError('נא להזין מספר טלפון לבקשת תשלום');
          return;
        }
        if (!isValidIsraeliPhone(alternatePaymentPhone)) {
          setAlternatePaymentPhoneError('מספר טלפון לא תקין. הזן מספר ישראלי תקין (למשל: 050-123-4567 או 0505456644)');
          return;
        }
      }

      setPaymentActionLoading(true);
      setPaymentSuccess('');
      // Enrich appointment data with fresh customer info from context
      const currentCustomer = customers.find(c => c.id === appointmentData.customer_id);
      const enrichedAppointmentData = {
        ...appointmentData,
        customer_name: currentCustomer?.full_name || appointmentData.customer_name,
        customer_phone: currentCustomer?.phone || appointmentData.customer_phone,
        customer_email: currentCustomer?.email || appointmentData.customer_email,
      };
      const paymentContext = PaymentAPI.generatePaymentContext(enrichedAppointmentData, user.uid);

      // Add custom invoice text if provided
      const customText = invoiceItemText.trim();
      if (customText) {
        paymentContext.invoiceItemText = customText;
      }

      // Use alternate payment phone if provided (normalized to digits only)
      if (useAlternatePaymentPhone && alternatePaymentPhone.trim()) {
        paymentContext.customerPhone = normalizePhone(alternatePaymentPhone);
      }

      // Add maximum payment installments if greater than 1
      if (maxPayments > 1) {
        paymentContext.maxPayments = maxPayments;
      }

      const result = await PaymentAPI.createPaymentLink(paymentContext);

      setPaymentSuccess('קישור תשלום נוצר בהצלחה!');
      console.log('Payment link created:', result);

    } catch (error) {
      console.error('Error requesting payment:', error);
      setErrorModalContent({
        title: 'שגיאה בבקשת תשלום',
        message: PaymentAPI.getErrorMessage(error)
      });
      setShowErrorModal(true);
    } finally {
      setPaymentActionLoading(false);
    }
  };

  const handleCancelPayment = async (paymentId) => {
    try {
      setPaymentActionLoading(true);
      await PaymentAPI.cancelPaymentRequest(user.uid, paymentId);
      setPaymentSuccess('בקשת התשלום בוטלה בהצלחה.');
    } catch (error) {
      setErrorModalContent({
        title: 'שגיאה בביטול תשלום',
        message: PaymentAPI.getErrorMessage(error)
      });
      setShowErrorModal(true);
    } finally {
      setPaymentActionLoading(false);
    }
  };

  const handleViewPayment = (payment) => {
    alert(`פרטי תשלום:\nסכום: ₪${payment.amount}\nתאריך: ${new Date(payment.completedAt || payment.createdAt).toLocaleDateString('he-IL')}`);
  };

  const refreshAppointmentFromBackend = useCallback(async () => {
    if (!appointment?.id) return;

    try {
      setRefreshingAppointment(true);
      const freshAppointment = await getAppointment(appointment.id, true);
      if (freshAppointment && freshAppointment.invoice_url) {
        invalidateCacheForDate(appointment.date);
        await forceRefreshCurrentRange();
      }
    } catch (error) {
      console.error('Error refreshing appointment:', error);
    } finally {
      setRefreshingAppointment(false);
    }
  }, [appointment?.id, getAppointment]);

  // Clear payment messages and invoice file when modal closes
  useEffect(() => {
    if (!isOpen) {
      setPaymentSuccess('');
      setInvoiceFile(null);
      setUploadingInvoice(false);
      setInvoiceItemText('');
      setUseAlternatePaymentPhone(false);
      setAlternatePaymentPhone('');
      setAlternatePaymentPhoneError('');
      setSkippedPaidInfo(null);
      // Don't reset invoiceJustUploaded here - we want to keep it until appointment changes
    }
  }, [isOpen]);

  // Reset invoiceJustUploaded when appointment changes or has an invoice
  useEffect(() => {
    if (appointment?.invoice_url) {
      // If appointment now has an invoice URL, reset the flag
      setInvoiceJustUploaded(false);
    }
  }, [appointment?.id, appointment?.invoice_url]);

  // Refresh appointment data when modal opens if we just uploaded an invoice
  useEffect(() => {
    if (isOpen && invoiceJustUploaded && appointment?.id && !appointment?.invoice_url) {
      console.log('Modal reopened after invoice upload, refreshing appointment data');
      refreshAppointmentFromBackend();
    }
  }, [isOpen, invoiceJustUploaded, appointment?.id, appointment?.invoice_url]);

  const {
    activeCrewMembers,
    getAvailableCrewMembersForService,
    loading: crewMembersLoading
  } = useCrewMembersPG();
  const { customers, searchCustomers, addCustomer, updateCustomer } = useCustomers();

  // Get the prefilled customer details for display
  const prefilledCustomer = React.useMemo(() => {
    if (prefilledCustomerId && customers.length > 0) {
      return customers.find(c => c.id === prefilledCustomerId);
    }
    return null;
  }, [prefilledCustomerId, customers]);

  // Resolve the currently selected customer from context, with fallback to appointment data
  const selectedCustomer = React.useMemo(() => {
    if (!formData.customer_id) return null;
    const fromContext = customers.find(c => c.id === formData.customer_id);
    if (fromContext) return fromContext;
    // Fallback when context hasn't loaded yet
    if (appointment && appointment.customer_id === formData.customer_id) {
      return {
        id: appointment.customer_id,
        full_name: appointment.customer_name || '',
        phone: appointment.customer_phone || '',
        email: appointment.customer_email || '',
      };
    }
    return null;
  }, [formData.customer_id, customers, appointment]);

  // Get automation executions for this appointment
  const { executionsByAppointment } = useAutomations();
  const appointmentExecutions = appointment?.id ? executionsByAppointment.get(appointment.id) || [] : [];

  const [filteredCustomers, setFilteredCustomers] = useState([]);

  useEffect(() => {
    let isMounted = true;

    const fetchFilteredCustomers = async () => {
      if (!customerSearchTerm.trim()) {
        setFilteredCustomers(customers.slice(0, 10));
        return;
      }

      try {
        const result = await searchCustomers(customerSearchTerm);
        if (isMounted) {
          setFilteredCustomers(result.slice(0, 10));
        }
      } catch (err) {
        console.error('Failed to search customers:', err);
        if (isMounted) setFilteredCustomers([]);
      }
    };

    fetchFilteredCustomers();

    return () => {
      isMounted = false;
    };
  }, [customerSearchTerm, customers, searchCustomers]);

  // Reset form when modal opens/closes or appointment changes
  React.useEffect(() => {
    if (isOpen) {
      if (appointment && mode === 'edit') {
        console.log('AppointmentModal Edit Mode - Appointment:', appointment);
        console.log('AppointmentModal Edit Mode - Customers array:', customers);
        console.log('AppointmentModal Edit Mode - Looking for customer_id:', appointment.customer_id);

        const hasCompletedPayment = paymentStatus === 'completed' ||
          payments?.some(p => p.status === 'completed');
        const hasInvoice = appointment.invoice_url;

        if (hasCompletedPayment && !hasInvoice && !refreshingAppointment) {
          console.log('AppointmentModal: Payment completed but no invoice, refreshing from backend');
          refreshAppointmentFromBackend();
        }

        // Determine service IDs for multi-service support
        let serviceIds = [];
        let appointmentServices = [];

        if (appointment.has_multiple_services && appointment.services) {
          // Multi-service appointment
          serviceIds = appointment.services.map(s => s.service_id);
          appointmentServices = appointment.services.map(s => services.find(svc => svc.id === s.service_id)).filter(Boolean);
        } else if (appointment.service_id) {
          // Single-service appointment (backward compatibility)
          serviceIds = [appointment.service_id];
          const svc = services.find(s => s.id === appointment.service_id);
          if (svc) appointmentServices = [svc];
        }

        // Populate form with existing appointment data
        setFormData({
          customer_id: appointment.customer_id || '',
          service_ids: serviceIds,
          crew_member_id: appointment.crew_member_id || '',
          date: appointment.date || appointment.date || '',
          time: appointment.time || appointment.time || '',
          duration: appointment.duration?.toString() || '',
          price: appointment.price?.toString() || '',
          status: appointment.status || 'scheduled',
          notes: appointment.notes || '',
          is_recurring: appointment.is_recurring || false,
          recurring_interval_weeks: appointment.recurring_pattern?.intervalWeeks || 1,
          recurring_end_type: appointment.recurring_pattern?.endType || 'count',
          recurring_end_count: appointment.recurring_pattern?.endValue || 1,
          recurring_end_date: appointment.recurring_pattern?.endType === 'date' ? appointment.recurring_pattern.endValue : '',
          is_paid: appointment.is_paid || false
        });

        // Set selected services
        setSelectedServices(appointmentServices);

        // Show series editing options if this is a recurring appointment
        if (appointment.is_recurring) {
          setShowSeriesOptions(true);
        }

        // Set customer search field
        if (appointment.customer_id) {
          const customer = customers.find(c => c.id === appointment.customer_id);
          const customerName = customer?.full_name || customer?.name || appointment.customer_name || '';
          setSelectedCustomerName(customerName);
          setCustomerSearchTerm(customerName);
        } else if (appointment.customer_name) {
          setSelectedCustomerName(appointment.customer_name);
          setCustomerSearchTerm(appointment.customer_name);
        }
      } else {
        // Reset form for new appointment with prefilled values
        setFormData({
          customer_id: prefilledCustomerId || '',
          service_ids: [],
          crew_member_id: activeCrewMembers.length === 1 ? activeCrewMembers[0].id : '',
          date: prefilledDate || '',
          time: prefilledTime || '',
          duration: '',
          price: '',
          status: 'scheduled',
          notes: '',
          is_recurring: false,
          recurring_interval_weeks: 1,
          recurring_end_type: 'count', // 'count' or 'date'
          recurring_end_count: 1,
          recurring_end_date: '',
          is_paid: false
        });

        // Set prefilled customer search
        if (prefilledCustomer) {
          setSelectedCustomerName(prefilledCustomer.full_name);
          setCustomerSearchTerm(prefilledCustomer.full_name);
        } else {
          setSelectedCustomerName('');
          setCustomerSearchTerm('');
        }

        setSelectedServices([]);
      }

      setErrors({});
      setSubmitSuccess(false);
      setShowDeleteConfirm(false);
      // NEW: Reset dropdown
      setShowCustomerDropdown(false);
    }
  }, [
    isOpen,
    mode,
    appointment?.id,
    prefilledDate,
    prefilledTime,
    prefilledCustomerId,
    services.length,
    activeCrewMembers.length]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (customerDropdownRef.current && !customerDropdownRef.current.contains(event.target)) {
        setShowCustomerDropdown(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const crewMemberOptions = React.useMemo(() => {
    // If no services are selected, show all active crew members (but grayed out)
    if (!formData.service_ids || formData.service_ids.length === 0) {
      return activeCrewMembers.map(member => ({
        value: member.id,
        label: member.name,
        color: member.color,
        disabled: true,  // Disable all if no service selected
        reason: t.selectServiceFirst
      }));
    }

    // For multi-service, crew member must be qualified for ALL selected services
    return activeCrewMembers.map(member => {
      let isQualified = true;

      for (const serviceId of formData.service_ids) {
        const qualifiedMembers = getAvailableCrewMembersForService(serviceId);
        const canPerformThis = qualifiedMembers.some(qm => qm.id === member.id);
        if (!canPerformThis) {
          isQualified = false;
          break;
        }
      }

      return {
        value: member.id,
        label: member.name,
        color: member.color,
        disabled: !isQualified,
        reason: !isQualified ? t.notQualifiedForService : null
      };
    });
  }, [activeCrewMembers, formData.service_ids, getAvailableCrewMembersForService, t]);

  const checkAvailabilityConflict = React.useCallback(() => {
    if (!formData.crew_member_id || !formData.date || !formData.time) {
      setAvailabilityConflict(null);
      return;
    }

    const crewMember = activeCrewMembers.find(cm => cm.id === formData.crew_member_id);
    if (!crewMember) {
      setAvailabilityConflict(null);
      return;
    }

    // Check both weekly availability and work frequency
    let conflict = null;

    // Check weekly availability (existing logic)
    if (crewMember.getAvailabilityConflict) {
      conflict = crewMember.getAvailabilityConflict(formData.date, formData.time);
    }

    // Check work frequency
    if (!conflict?.hasConflict && crewMember.isWorkingOnWeek) {
      const isWorkingThisWeek = crewMember.isWorkingOnWeek(formData.date);
      if (!isWorkingThisWeek) {
        conflict = {
          hasConflict: true,
          conflictType: 'work_frequency',
          workFrequencyWeeks: crewMember.work_frequency_weeks,
          workReferenceDate: crewMember.work_reference_date
        };
      }
    }

    setAvailabilityConflict(conflict?.hasConflict ? conflict : null);
  }, [formData.crew_member_id, formData.date, formData.time, activeCrewMembers]);

  React.useEffect(() => {
    checkAvailabilityConflict();
  }, [formData.crew_member_id, formData.date, formData.time]); // Only depend on the form fields

  // Handle customer search input changes
  const handleCustomerSearch = (e) => {
    const value = e.target.value;
    setCustomerSearchTerm(value);
    setShowCustomerDropdown(true);

    // Clear selection if user is typing something different
    if (value !== selectedCustomerName) {
      setFormData(prev => ({ ...prev, customer_id: '' }));
      setSelectedCustomerName('');
    }
  };

  // Handle customer selection from dropdown
  const handleCustomerSelect = (customer) => {
    setFormData(prev => ({ ...prev, customer_id: customer.id }));
    setSelectedCustomerName(customer.full_name);
    setCustomerSearchTerm(customer.full_name);
    setShowCustomerDropdown(false);

    // Clear any customer selection errors
    if (errors.customer_id) {
      setErrors(prev => ({ ...prev, customer_id: null }));
    }
  };

  // Handle new customer creation from within the appointment modal
  const handleNewCustomerSubmit = async (customerData) => {
    const newCustomer = await addCustomer(customerData);
    handleCustomerSelect(newCustomer);
    setIsAddCustomerModalOpen(false);
  };

  // Handle customer edit from within the appointment modal
  const handleEditCustomerSubmit = async (customerId, updateData) => {
    const updatedCustomer = await updateCustomer(customerId, updateData);
    handleCustomerSelect(updatedCustomer);
    if (onCustomerUpdated) onCustomerUpdated(updatedCustomer);
    setIsEditCustomerModalOpen(false);
  };

  // Handle keyboard navigation for customer search
  const handleCustomerSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (filteredCustomers.length > 0) {
        handleCustomerSelect(filteredCustomers[0]);
      }
    } else if (e.key === 'Escape') {
      setShowCustomerDropdown(false);
    }
  };

  // Convert services to options for dropdown
  const serviceOptions = React.useMemo(() => {
    return services
      .filter(service => service.is_active)
      .map(service => ({
        value: service.id,
        label: `${service.name} - ₪${service.price} (${service.duration} ${t.minutes})`
      }));
  }, [services]);

  // Generate time options in 10-minute intervals
  const timeOptions = React.useMemo(() => {
    const options = [];
    for (let hour = 6; hour <= 23; hour++) {
      for (let minute = 0; minute < 60; minute += 10) {
        const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        options.push(timeString);
      }
    }
    return options;
  }, []);

  // Handle multi-service selection
  const handleServicesChange = (serviceIds) => {
    // Prevent duplicate service selection
    const uniqueServiceIds = [...new Set(serviceIds)];
    const selectedServs = services.filter(s => uniqueServiceIds.includes(s.id));

    // Calculate totals (sum - sequential execution)
    const totalPrice = selectedServs.reduce((sum, s) => sum + (parseFloat(s.price) || 0), 0);
    const totalDuration = selectedServs.reduce((sum, s) => sum + (parseInt(s.duration) || 0), 0);

    // Update form data
    const newFormData = {
      ...formData,
      service_ids: uniqueServiceIds,
      price: totalPrice.toString(),
      duration: totalDuration.toString()
    };

    // Validate crew member can perform ALL selected services
    if (formData.crew_member_id && uniqueServiceIds.length > 0) {
      let allQualified = true;
      for (const serviceId of uniqueServiceIds) {
        const qualifiedMembers = getAvailableCrewMembersForService(serviceId);
        const isQualified = qualifiedMembers.some(member => member.id === formData.crew_member_id);
        if (!isQualified) {
          allQualified = false;
          break;
        }
      }

      // If crew member can't perform all services, clear selection
      if (!allQualified) {
        newFormData.crew_member_id = '';
        setErrors(prev => ({
          ...prev,
          crew_member_id: t.selectedMemberNotQualified
        }));
      }
    }

    setFormData(newFormData);
    setSelectedServices(selectedServs);

    // Clear error for service selection
    if (errors.service_ids) {
      setErrors(prev => ({
        ...prev,
        service_ids: ''
      }));
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.customer_id) {
      newErrors.customer_id = t.customerRequired;
    }

    // Validate services (at least one required)
    if (!formData.service_ids || formData.service_ids.length === 0) {
      newErrors.service_ids = t.serviceRequired;
    }

    if (!formData.crew_member_id) {
      newErrors.crew_member_id = t.crewMemberRequired;
    }

    // Validate that crew member can perform ALL selected services
    if (formData.service_ids && formData.service_ids.length > 0 && formData.crew_member_id) {
      for (const serviceId of formData.service_ids) {
        const qualifiedMembers = getAvailableCrewMembersForService(serviceId);
        const isQualified = qualifiedMembers.some(member => member.id === formData.crew_member_id);

        if (!isQualified) {
          newErrors.crew_member_id = t.selectedMemberCannotPerformService;
          break;
        }
      }
    }

    if (formData.is_recurring) {
      if (!formData.recurring_interval_weeks || isNaN(formData.recurring_interval_weeks) || parseInt(formData.recurring_interval_weeks) < 1) {
        newErrors.recurring_interval_weeks = 'מרווח חוקי נדרש';
      }

      if (formData.recurring_end_type === 'count') {
        if (!formData.recurring_end_count || isNaN(formData.recurring_end_count) || parseInt(formData.recurring_end_count) < 1) {
          newErrors.recurring_end_count = 'מספר חזרות חוקי נדרש';
        }
      } else if (formData.recurring_end_type === 'date') {
        if (!formData.recurring_end_date) {
          newErrors.recurring_end_date = 'תאריך סיום נדרש';
        } else {
          const endDate = new Date(formData.recurring_end_date);
          const startDate = new Date(formData.date);
          if (endDate <= startDate) {
            newErrors.recurring_end_date = 'תאריך הסיום חייב להיות אחרי תאריך ההתחלה';
          }
        }
      }
    }

    if (!formData.date) {
      newErrors.date = t.dateRequired;
    }

    if (!formData.time) {
      newErrors.time = t.timeRequired;
    }

    if (!formData.duration || isNaN(formData.duration) || parseInt(formData.duration) <= 0) {
      newErrors.duration = t.validDurationRequired;
    }

    if (!formData.price || isNaN(formData.price) || parseFloat(formData.price) < 0) {
      newErrors.price = t.validPriceRequired;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrors({});

    try {
      // Validate form
      if (!validateForm()) {
        // Map of field names to their refs and error messages
        const fieldRefs = [
          { name: 'customer_id', ref: customerSearchRef, label: 'לקוח' },
          { name: 'service_ids', ref: serviceSelectRef, label: 'שירותים' },
          { name: 'crew_member_id', ref: crewMemberRef, label: 'חבר צוות' },
          { name: 'date', ref: dateRef, label: 'תאריך' },
          { name: 'time', ref: timeRef, label: 'שעה' },
          { name: 'duration', ref: durationRef, label: 'משך זמן' },
          { name: 'price', ref: priceRef, label: 'מחיר' },
          { name: 'recurring_interval_weeks', ref: null, label: 'מרווח חזרות' },
          { name: 'recurring_end_count', ref: null, label: 'מספר חזרות' },
          { name: 'recurring_end_date', ref: null, label: 'תאריך סיום חזרות' }
        ];

        // Find the first field with an error
        let firstErrorField = null;
        let firstErrorMessage = '';

        for (const field of fieldRefs) {
          // Check if this field has an error
          const hasError = errors[field.name];

          // Special check for service_ids array
          if (field.name === 'service_ids' && (!formData.service_ids || formData.service_ids.length === 0)) {
            firstErrorField = field.ref;
            firstErrorMessage = `נא לבחור לפחות ${field.label} אחד`;
            break;
          } else if (hasError) {
            firstErrorField = field.ref;
            firstErrorMessage = errors[field.name];
            break;
          }
        }

        // Scroll to first error field
        if (firstErrorField && firstErrorField.current) {
          firstErrorField.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }

        // Show error modal with specific message
        setErrorModalContent({
          title: 'שגיאה בטופס',
          message: firstErrorMessage || 'נא למלא את כל השדות הנדרשים.'
        });
        setShowErrorModal(true);
        return;
      }

      // Get related objects - check customers first to include newly created customers
      const customer = customers.find(c => c.id === formData.customer_id);
      const crewMember = activeCrewMembers.find(cm => cm.id === formData.crew_member_id);

      // Prepare services array for backend
      const servicesArray = selectedServices.map((svc, index) => ({
        service_id: svc.id,
        service_name: svc.name,
        price: svc.price,
        duration: svc.duration,
        sequence_order: index + 1
      }));

      const appointmentData = {
        customer_id: formData.customer_id,
        services: servicesArray,  // Multi-service array
        crew_member_id: formData.crew_member_id,
        date: formData.date,
        time: formData.time,
        duration: parseInt(formData.duration),
        price: parseFloat(formData.price),
        status: formData.status,
        notes: formData.notes || '',
        customer_name: customer?.full_name || '',
        customer_phone: customer?.phone || '',
        crew_member_name: crewMember?.name || '',
        is_paid: formData.is_paid,
        is_recurring: formData.is_recurring,
        recurring_pattern: formData.is_recurring ? {
          intervalWeeks: parseInt(formData.recurring_interval_weeks),
          endType: formData.recurring_end_type,
          endValue: formData.recurring_end_type === 'count'
            ? parseInt(formData.recurring_end_count)
            : formData.recurring_end_date
        } : null,
        seriesEditMode: appointment?.is_recurring ? seriesEditMode : undefined
      };

      let result;
      if (mode === 'edit' && appointment) {
        result = await onSuccess({ id: appointment.id, ...appointmentData }, 'edit');
      } else {
        result = await onSuccess(appointmentData, 'add');
      }

      // Check if there were skipped paid appointments in series edit
      if (result && result.skippedPaidAppointments && result.skippedPaidAppointments.length > 0) {
        setSkippedPaidInfo({
          type: 'edit',
          count: result.skippedPaidCount || result.skippedPaidAppointments.length,
          appointments: result.skippedPaidAppointments
        });
      }

      if (result && (result.success !== false)) {
        // Handle invoice upload for cash payments
        if (invoiceFile && formData.is_paid && paymentStatus !== 'completed') {
          try {
            setUploadingInvoice(true);
            console.log('Uploading invoice for cash payment');

            // Get the appointment ID from the result
            const appointmentId = mode === 'edit' ? appointment.id : (Array.isArray(result) ? result[0].id : result.id);

            // Upload invoice to Firebase Storage
            const invoiceUrl = await uploadInvoiceToStorage(invoiceFile, appointmentId, user.uid);

            // Update appointment with invoice URL via API
            await AppointmentAPI.updateAppointmentInvoice(appointmentId, {
              invoiceUrl,
              invoiceNumber: manualInvoiceNumber || null,
              transactionId: null,
              processId: null
            });

            console.log('Invoice uploaded and appointment updated successfully');

            // Invalidate cache and force refresh to update the appointments list
            await invalidateCacheForDate(appointmentData.date);
            await forceRefreshCurrentRange();

            // Mark invoice as uploaded and clear the file state
            setInvoiceJustUploaded(true);
            setInvoiceFile(null);
            setManualInvoiceNumber('');

          } catch (uploadError) {
            console.error('Error uploading invoice:', uploadError);
            setErrorModalContent({
              title: 'התור נשמר אך החשבונית לא הועלתה',
              message: `התור נשמר בהצלחה, אך אירעה שגיאה בהעלאת החשבונית: ${uploadError.message}`
            });
            setShowErrorModal(true);
            setUploadingInvoice(false);
            // Don't close the modal - let user dismiss error and try again or close manually
            return;
          } finally {
            setUploadingInvoice(false);
          }
        }

        // Only close modal if everything succeeded
        setSubmitSuccess(true);
        setTimeout(() => {
          onClose();
        }, 200);
      } else {
        // Handle case where result indicates failure
        throw new Error(result?.error || 'Operation failed');
      }

    } catch (error) {
      console.error('Error saving appointment:', error);

      // Check for special cancellation case
      if (error.message.includes('בוטל') || error.message.includes('cancelled')) {
        if (formData.status === 'cancelled') {
          console.warn('Cancellation may have succeeded despite error');
          setSubmitSuccess(true);
          setTimeout(() => {
            onClose();
          }, 200);
          return;
        }
      }

      // Get user-friendly error message based on error code (pass details for context-specific messages)
      const errorInfo = error.errorCode
        ? getErrorMessage(error.errorCode, error.details)
        : { message: error.message || 'אירעה שגיאה בשמירת התור' };

      setErrorModalContent({
        title: errorInfo.title || 'שגיאה בשמירת התור',
        message: errorInfo.message
      });
      setShowErrorModal(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!appointment) return;

    setIsDeleting(true);
    try {
      // Pass the delete mode for recurring appointments
      const appointmentWithDeleteMode = {
        ...appointment,
        deleteMode: appointment.is_recurring ? seriesDeleteMode : 'single'
      };

      const result = await onSuccess(appointmentWithDeleteMode, 'delete');

      // Check if there were skipped paid appointments in series delete
      if (result && result.skippedPaidAppointments && result.skippedPaidAppointments.length > 0) {
        setSkippedPaidInfo({
          type: 'delete',
          count: result.skippedPaidAppointments.length,
          appointments: result.skippedPaidAppointments
        });
      }

      setSubmitSuccess(true);
      setTimeout(() => {
        onClose();
      }, 1000);
    } catch (error) {
      console.error('Error deleting appointment:', error);
      setErrorModalContent({
        title: 'שגיאה במחיקת התור',
        message: error.message || 'Failed to delete appointment'
      });
      setShowErrorModal(true);
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  // Compute the effective payment status for the badge
  // This considers both payment link status and manual payment status
  const effectivePaymentStatus = React.useMemo(() => {
    if (mode !== 'edit' || !appointment) return 'not_requested';

    // If there's an invoice (from payment link or manual upload), it's completed
    const hasInvoice = appointment.invoice_url !== null && appointment.invoice_url !== undefined;
    if (hasInvoice) return 'completed';

    // If manually marked as paid (is_paid checkbox), it's completed
    if (appointment.is_paid || formData.is_paid) return 'completed';

    // Otherwise, use the payment link status
    return paymentStatus;
  }, [mode, appointment, paymentStatus, formData.is_paid]);

  // Check if appointment has payment restrictions - disables ALL fields except status and notes
  const hasPaymentRestriction = React.useMemo(() => {
    if (mode !== 'edit' || !appointment) return false;

    // If payment action is in progress (creating payment link), restrict editing
    if (paymentActionLoading) return true;

    // If an invoice exists, restrict editing (regardless of payment method)
    const hasInvoice = appointment.invoice_url !== null && appointment.invoice_url !== undefined;

    // If payment is completed via payment link, restrict editing
    const hasCompletedPayment = paymentStatus === 'completed' ||
      payments?.some(p => p.status === 'completed');

    // If payment is pending via payment link, restrict editing
    const hasPendingPayment = paymentStatus === 'pending' ||
      payments?.some(p => p.status === 'pending');

    // If there's an invoice (manual upload or from payment), restrict ALL editing except status and notes
    if (hasInvoice) return true;

    // If invoice was just uploaded in this session, restrict editing immediately
    if (invoiceJustUploaded) return true;

    // If payment is completed or pending, restrict editing
    return hasCompletedPayment || hasPendingPayment || paymentActionLoading;
  }, [mode, appointment, paymentStatus, payments, paymentActionLoading, invoiceJustUploaded]);

  // Check if "payment received" checkbox should be disabled
  const isPaymentCheckboxDisabled = React.useMemo(() => {
    if (mode !== 'edit' || !appointment) return false;

    // If payment action is in progress, disable
    if (paymentActionLoading) return true;

    // If there's a pending payment link, disable
    if (paymentStatus === 'pending') return true;

    // If there's an invoice (manual or from payment link), disable permanently
    const hasInvoice = appointment.invoice_url !== null && appointment.invoice_url !== undefined;
    if (hasInvoice) return true;

    // If invoice was just uploaded in this session, disable permanently
    if (invoiceJustUploaded) return true;

    // Otherwise, allow toggling (user can check/uncheck before invoice upload)
    return false;
  }, [mode, appointment, paymentStatus, paymentActionLoading, invoiceJustUploaded]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto overflow-x-hidden">

        <AppointmentModalHeader
          mode={mode}
          appointment={appointment}
          effectivePaymentStatus={effectivePaymentStatus}
          onClose={onClose}
          prefilledCustomer={prefilledCustomer}
          t={t}
        />

        <div className="flex-1 overflow-y-auto p-6">
          <AppointmentFormNotifications
            paymentSuccess={paymentSuccess}
            skippedPaidInfo={skippedPaidInfo}
            hasPaymentRestriction={hasPaymentRestriction}
            paymentActionLoading={paymentActionLoading}
            onDismissSkippedInfo={() => setSkippedPaidInfo(null)}
          />

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 min-w-0">

              {(!prefilledCustomerId || mode === 'edit') && (
                <div className="md:col-span-2">
                  <CustomerSelector
                    customerSearchTerm={customerSearchTerm}
                    showCustomerDropdown={showCustomerDropdown}
                    filteredCustomers={filteredCustomers}
                    selectedCustomerId={formData.customer_id}
                    hasPaymentRestriction={hasPaymentRestriction}
                    error={errors.customer_id}
                    customerSearchRef={customerSearchRef}
                    customerDropdownRef={customerDropdownRef}
                    onSearchChange={handleCustomerSearch}
                    onCustomerSelect={handleCustomerSelect}
                    onSearchKeyDown={handleCustomerSearchKeyDown}
                    onFocus={() => !hasPaymentRestriction && setShowCustomerDropdown(true)}
                    onAddCustomer={() => setIsAddCustomerModalOpen(true)}
                    onEditCustomer={() => setIsEditCustomerModalOpen(true)}
                    onViewCustomer={() => setIsCustomerDetailModalOpen(true)}
                    t={t}
                  />
                </div>
              )}

              <ServiceCrewSelector
                serviceIds={formData.service_ids}
                serviceOptions={serviceOptions}
                serviceError={errors.service_ids}
                serviceSelectRef={serviceSelectRef}
                onServicesChange={handleServicesChange}
                crewMemberId={formData.crew_member_id}
                crewMemberOptions={crewMemberOptions}
                crewMemberError={errors.crew_member_id}
                crewMemberRef={crewMemberRef}
                onCrewChange={handleInputChange}
                hasPaymentRestriction={hasPaymentRestriction}
                availabilityConflict={availabilityConflict}
                selectedCrewMemberName={activeCrewMembers.find(cm => cm.id === formData.crew_member_id)?.name || ''}
                appointmentDate={formData.date}
                appointmentTime={formData.time}
                t={t}
              />

              <AppointmentScheduleFields
                date={formData.date}
                time={formData.time}
                duration={formData.duration}
                price={formData.price}
                status={formData.status}
                mode={mode}
                hasPaymentRestriction={hasPaymentRestriction}
                shouldSkipPayment={shouldSkipPayment}
                dateError={errors.date}
                timeError={errors.time}
                durationError={errors.duration}
                priceError={errors.price}
                dateRef={dateRef}
                timeRef={timeRef}
                durationRef={durationRef}
                priceRef={priceRef}
                timeOptions={timeOptions}
                onInputChange={handleInputChange}
                t={t}
              />

              <div className="md:col-span-2">
                <RecurringAppointmentConfig
                  isRecurring={formData.is_recurring}
                  recurringIntervalWeeks={formData.recurring_interval_weeks}
                  recurringEndType={formData.recurring_end_type}
                  recurringEndCount={formData.recurring_end_count}
                  recurringEndDate={formData.recurring_end_date}
                  startDate={formData.date}
                  hasPaymentRestriction={hasPaymentRestriction}
                  intervalWeeksError={errors.recurring_interval_weeks}
                  endCountError={errors.recurring_end_count}
                  endDateError={errors.recurring_end_date}
                  showSeriesOptions={showSeriesOptions}
                  isRecurringAppointment={appointment?.is_recurring}
                  seriesEditMode={seriesEditMode}
                  onToggleRecurring={(checked) => setFormData(prev => ({ ...prev, is_recurring: checked }))}
                  onIntervalChange={(v) => setFormData(prev => ({ ...prev, recurring_interval_weeks: v }))}
                  onEndTypeChange={(v) => setFormData(prev => ({ ...prev, recurring_end_type: v }))}
                  onEndCountChange={(v) => setFormData(prev => ({ ...prev, recurring_end_count: v }))}
                  onEndDateChange={(v) => setFormData(prev => ({ ...prev, recurring_end_date: v }))}
                  onSeriesEditModeChange={setSeriesEditMode}
                />
              </div>

              {/* Notes */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t.notes}
                </label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  rows="3"
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-pink-500 focus:border-pink-500"
                  placeholder={t.additionalNotesPlaceholder}
                />
              </div>

              <AppointmentPaymentSection
                shouldSkipPayment={shouldSkipPayment}
                paymentsEnabled={config.settings?.paymentsEnabled === true}
                mode={mode}
                appointment={appointment}
                user={user}
                appointmentStatus={formData.status}
                isPaid={formData.is_paid}
                isPaymentCheckboxDisabled={isPaymentCheckboxDisabled}
                paymentStatus={paymentStatus}
                paymentActionLoading={paymentActionLoading}
                onIsPaidChange={(checked) => setFormData(prev => ({ ...prev, is_paid: checked }))}
                invoiceFile={invoiceFile}
                uploadingInvoice={uploadingInvoice}
                invoiceJustUploaded={invoiceJustUploaded}
                onInvoiceFileChange={(file) => {
                  if (file) {
                    setPendingInvoiceFile(file);
                    setManualInvoiceNumber('');
                    setShowInvoiceNumberModal(true);
                  }
                }}
                onClearInvoiceFile={() => {
                  setInvoiceFile(null);
                  setPendingInvoiceFile(null);
                  setManualInvoiceNumber('');
                }}
                invoiceItemText={invoiceItemText}
                useAlternatePaymentPhone={useAlternatePaymentPhone}
                alternatePaymentPhone={alternatePaymentPhone}
                alternatePaymentPhoneError={alternatePaymentPhoneError}
                maxPayments={maxPayments}
                payments={payments}
                paymentLoading={paymentLoading}
                paymentError={paymentError}
                formErrors={errors}
                onInvoiceItemTextChange={setInvoiceItemText}
                onToggleAlternatePhone={(checked) => {
                  setUseAlternatePaymentPhone(checked);
                  if (!checked) {
                    setAlternatePaymentPhone('');
                    setAlternatePaymentPhoneError('');
                  }
                }}
                onAlternatePhoneChange={(v) => {
                  setAlternatePaymentPhone(v);
                  if (alternatePaymentPhoneError) setAlternatePaymentPhoneError('');
                }}
                onAlternatePhoneBlur={() => {
                  if (alternatePaymentPhone.trim() && !isValidIsraeliPhone(alternatePaymentPhone)) {
                    setAlternatePaymentPhoneError('מספר טלפון לא תקין. הזן מספר ישראלי תקין (למשל: 050-123-4567 או 0505456644)');
                  }
                }}
                onMaxPaymentsChange={setMaxPayments}
                onRequestPayment={handleRequestPayment}
                onCancelPayment={handleCancelPayment}
                onViewPayment={handleViewPayment}
              />

            </div>
          </form>

          {mode === 'edit' && appointment && !shouldSkipPayment && (
            <AutomationExecutions executions={appointmentExecutions} />
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
          <div>
            {mode === 'edit' && (
              <Button
                variant="danger"
                onClick={() => setShowDeleteConfirm(true)}
                disabled={isSubmitting || isDeleting || hasPaymentRestriction}
                className="mx-2"
              >
                {isDeleting ? t.deleting : t.delete}
              </Button>
            )}
          </div>

          <div className="flex space-x-3">
            <Button
              variant="secondary"
              onClick={onClose}
              disabled={isSubmitting || isDeleting || uploadingInvoice}
              className="mx-2"
            >
              {t.close}
            </Button>
            <Button
              type="submit"
              onClick={handleSubmit}
              disabled={isSubmitting || isDeleting || uploadingInvoice}
            >
              {uploadingInvoice ? 'מעלה חשבונית...' : isSubmitting ? t.saving : mode === 'edit' ? t.updateAppointment : t.bookAppointment}
            </Button>
          </div>
        </div>

        {/* Invoice Number Modal */}
        {showInvoiceNumberModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-60">
            <div className="bg-white rounded-lg shadow-xl max-w-sm w-full p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">הזן מספר חשבונית</h3>
              <p className="text-sm text-gray-500 mb-4">הזן את מספר החשבונית שיישמר עם התור</p>
              <input
                type="text"
                value={manualInvoiceNumber}
                onChange={(e) => setManualInvoiceNumber(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && manualInvoiceNumber.trim()) {
                    setInvoiceFile(pendingInvoiceFile);
                    setPendingInvoiceFile(null);
                    setShowInvoiceNumberModal(false);
                  } else if (e.key === 'Escape') {
                    setPendingInvoiceFile(null);
                    setManualInvoiceNumber('');
                    setShowInvoiceNumberModal(false);
                  }
                }}
                autoFocus
                placeholder="לדוגמה: 12345"
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-pink-500 focus:border-pink-500 text-sm mb-4"
                dir="ltr"
              />
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setPendingInvoiceFile(null);
                    setManualInvoiceNumber('');
                    setShowInvoiceNumberModal(false);
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  ביטול
                </button>
                <button
                  type="button"
                  disabled={!manualInvoiceNumber.trim()}
                  onClick={() => {
                    setInvoiceFile(pendingInvoiceFile);
                    setPendingInvoiceFile(null);
                    setShowInvoiceNumberModal(false);
                  }}
                  className="px-4 py-2 text-sm font-medium text-white bg-pink-600 border border-transparent rounded-md hover:bg-pink-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  אישור
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Error Modal */}
        {showErrorModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-60">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">{errorModalContent.title}</h3>
              <p className="text-gray-600 mb-6">{errorModalContent.message}</p>
              <div className="flex justify-end">
                <Button
                  variant="primary"
                  onClick={() => setShowErrorModal(false)}
                >
                  {t.close || 'סגור'}
                </Button>
              </div>
            </div>
          </div>
        )}

        <AppointmentDeleteModal
          isOpen={showDeleteConfirm}
          appointment={appointment}
          isDeleting={isDeleting}
          seriesDeleteMode={seriesDeleteMode}
          onSeriesDeleteModeChange={setSeriesDeleteMode}
          onConfirm={handleDelete}
          onCancel={() => setShowDeleteConfirm(false)}
          t={t}
        />

        {/* Customer Detail Modal */}
        <CustomerDetailModal
          isOpen={isCustomerDetailModalOpen}
          onClose={() => setIsCustomerDetailModalOpen(false)}
          customer={selectedCustomer}
        />

        {/* Add New Customer Modal */}
        <CustomerModal
          isOpen={isAddCustomerModalOpen}
          onClose={() => setIsAddCustomerModalOpen(false)}
          onSubmit={handleNewCustomerSubmit}
          mode="add"
        />

        {/* Edit Customer Modal */}
        <CustomerModal
          isOpen={isEditCustomerModalOpen}
          onClose={() => setIsEditCustomerModalOpen(false)}
          onSubmit={handleEditCustomerSubmit}
          customer={selectedCustomer}
          mode="edit"
        />
      </div>
    </div>
  );
}
